<template>
  <Page>
    <Head v-if="categoryData">
      <Title>{{ categoryData.metaData.pageTitle }}</Title>

      <Meta hid="description" name="description" vmid="description" :content="categoryData.metaData.metaDescription" />
      <Meta
        v-for="tag in formatOpenGraph(categoryData.metaData.openGraph)"
        :key="tag.hid"
        :property="tag.property"
        :content="tag.content" />
      <Link
        v-for="(tag) in formatCanonical(categoryData.metaData)"
        :key="tag.key"
        :rel="tag.rel"
        :hreflang="tag.hreflang"
        :href="tag.href" />
    </Head>
    <component :is="componentCategory" :category-data="categoryData" />
  </Page>
</template>
<script setup>
import { formatCanonical, formatOpenGraph } from '~/helpers/metadata'
const config = useRuntimeConfig()
const route = useRoute()
const { category, setCategory } = useProducts()
const { getCategoryBySlug, getSubCategoryBySlug, navigation } = useNavigation()
const categoryData = ref()
const { $lang, $api } = useNuxtApp()

const navigationCategory = getCategoryBySlug(route.params.slug[0]?.toLowerCase())
const navigationSubCategory = getSubCategoryBySlug(route.params.slug[0]?.toLowerCase(), route.params.slug[1]?.toLowerCase())

// Route validation
let navigating = false

// @TODO: Turn this logic into a middleware for this page only. This allows use to return early instead of keeping a navigating variable.
if (!navigationCategory || (route.params.slug[1] && !navigationSubCategory)) {
  // Look for redirects
  const data = await $api(`${config.public.cdnURL}storage/redirect/navigation.json`)
  if (data) {
    const found = data.find(row => {
      return route.path === row.from
    })

    if (found) {
      navigating = true
      if (found.to.startsWith('http')) {
        await navigateTo(found.to, {
          external: true
        })
      } else {
        // If 'found.to' has query parameter, add it to 'route.query'
        const urlParts = found.to.split('?')
        if (urlParts.length > 1) {
          const searchParams = new URLSearchParams(urlParts[1])
          searchParams.forEach((val, key) => {
            route.query[key] = val
          })
        }

        await navigateTo({ path: found.to, query: route.query })
      }
    }
  }

  // Look for language slugs
  if (!navigating) {
    navigation.value.forEach(item => {
      const found = Object.entries(item.slugs).find(slug => {
        return slug[1] === route.params.slug[0]?.toLowerCase()
      })
      if (found) {
        navigating = true
        return navigateTo(`/${found[0]}/${found[1]}`, { external: true })
      }
    })
  }
}

//  Redirect slugs to other languages
if (navigationCategory && !navigating) {
  const validSlug = Object.entries(navigationCategory.slugs).find(slug => {
    return (
      slug[0] === $lang && slug[1] === route.params.slug[0]?.toLowerCase()
    )
  })
  if (!validSlug) {
    const redirectToSlug = Object.entries(category.slugs).find(slug => {
      return slug[1] === route.params.slug[1]?.toLowerCase()
    })
    await navigateTo(`/${redirectToSlug[0]}/${redirectToSlug[1]}`)
  }
}

const componentCategory = computed(() => {
  if (navigating) { return } // If already navigating, component should not render
  if (categoryData.value?.action === 'collections') {
    return resolveComponent('categoryCollections')
  }
  return resolveComponent('categoryProductOverview')
})

if (!navigationSubCategory?.id && !navigationCategory?.id && !navigating) {
  throw showError({ statusCode: 404, statusMessage: 'category_not_found3' })
}

try {
  const data = await $api(
  `navigation/${navigationSubCategory?.id || navigationCategory?.id}`
  )

  if (data) {
    categoryData.value = data
  }
} catch {
  if (!navigating) {
    throw showError({ statusCode: 404, statusMessage: 'category_not_found4' })
  }
}

await setCategory({
  categorySlug: route.params.slug[0]?.toLowerCase(),
  subCategorySlug: route.params.slug[1]?.toLowerCase() || null
})

if (!category.value && !navigating) {
  throw showError({ statusCode: 404, statusMessage: 'category_not_found5' })
}

if (categoryData.value) {
  const schemaOrg = toRaw(categoryData.value).metaData.schemaOrg
  await useHead({
    titleTemplate: () => '%s',
    script: [
      ...schemaOrg.map(schemaEntry => {
        return {
          type: 'application/ld+json',
          children: JSON.stringify(schemaEntry)
        }
      })
    ]
  })
}

</script>
